import expressionsFr from "../assets/text/taverneu.fr.json";
import type { Color, GameStatus, HistoryEntry } from "./types";

export type TaverneuCategory =
  | "game_start"
  | "good_moves"
  | "bad_moves"
  | "taunts"
  | "boasts"
  | "anger_and_betrayal"
  | "absurd";

export interface TaverneuLine {
  category: TaverneuCategory;
  text: string;
}

const EXPRESSIONS = expressionsFr as Record<TaverneuCategory, string[]>;

const COOLDOWN_MS = 5000;
const MAJOR_CAPTURE_CHANCE = 0.85;
const MINOR_CAPTURE_CHANCE = 0.55;
const CHECK_CHANCE = 0.6;
const CASTLE_CHANCE = 0.5;
const PROMOTION_CHANCE = 0.85;
const IDLE_CHANCE = 0.12;

function shuffled<T>(items: readonly T[]): T[] {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

/** Pioche sans remise dans chaque categorie ("sac" melange) : pas de repetition avant d'avoir vu toutes les repliques. */
class LineBag {
  private bags = new Map<TaverneuCategory, string[]>();

  draw(category: TaverneuCategory): string {
    let bag = this.bags.get(category);
    if (!bag || bag.length === 0) {
      bag = shuffled(EXPRESSIONS[category]);
      this.bags.set(category, bag);
    }
    return bag.pop()!;
  }
}

/**
 * Decide si/quoi le Taverneu commente en reaction aux coups joues, du point de
 * vue du joueur "vedette" (l'humain, ou les Blancs par defaut en hot-seat).
 * Purement logique : c'est a l'appelant d'afficher la ligne retournee.
 */
export class TaverneuDirector {
  private bag = new LineBag();
  private lastShownAt = -Infinity;

  constructor(private starColor: Color) {}

  private tryCooldown(now: number): boolean {
    if (now - this.lastShownAt < COOLDOWN_MS) return false;
    this.lastShownAt = now;
    return true;
  }

  private line(category: TaverneuCategory): TaverneuLine {
    return { category, text: this.bag.draw(category) };
  }

  /** Toujours affiche (pas de chance/cooldown) : accueil en tout debut de partie. */
  greet(): TaverneuLine {
    this.lastShownAt = performance.now();
    return this.line("game_start");
  }

  /** A appeler apres chaque coup applique, avec le statut de partie resultant. */
  onMove(entry: HistoryEntry, status: GameStatus, now: number = performance.now()): TaverneuLine | null {
    const mover = entry.stateBefore.turn;
    const isStar = mover === this.starColor;

    if (status === "checkmate") {
      this.lastShownAt = now;
      return this.line(isStar ? "boasts" : "anger_and_betrayal");
    }

    if (entry.move.promotion) {
      if (Math.random() < PROMOTION_CHANCE && this.tryCooldown(now)) {
        return this.line(isStar ? "boasts" : "anger_and_betrayal");
      }
    }

    if (entry.move.captured) {
      const major = entry.move.captured.type === "q" || entry.move.captured.type === "r";
      const chance = major ? MAJOR_CAPTURE_CHANCE : MINOR_CAPTURE_CHANCE;
      if (Math.random() < chance && this.tryCooldown(now)) {
        if (isStar) return this.line(major ? "boasts" : "good_moves");
        return this.line(major ? "anger_and_betrayal" : Math.random() < 0.5 ? "bad_moves" : "taunts");
      }
    }

    if (status === "check") {
      if (Math.random() < CHECK_CHANCE && this.tryCooldown(now)) {
        if (isStar) return this.line("good_moves");
        return this.line(Math.random() < 0.5 ? "bad_moves" : "taunts");
      }
    }

    if (entry.move.castle && isStar) {
      if (Math.random() < CASTLE_CHANCE && this.tryCooldown(now)) {
        return this.line("good_moves");
      }
    }

    if (isStar && Math.random() < IDLE_CHANCE && this.tryCooldown(now)) {
      return this.line("absurd");
    }

    return null;
  }
}
